import React, { FC, memo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPlus } from '@fortawesome/free-solid-svg-icons'

import BootstrapModalLayout from '../../../ui/BootstrapModalLayout'
import { Button } from '../../../common/components/Button'
import { CloseIcon } from '../../../common/icons/close-icon/CloseIcon'
import Input from '../../../common/components/Input'
import { Table, TableBody, TableBodyCell, TableHeader, TableHeaderCell, TableRow } from '../../../lib/table'
import {
    MeasurementDevicesGetModel,
    useCompanyAddMeasurementDeviceToCompanyMutation,
    useMeasurementDevicesGetFilteredQuery,
} from '../../../common/redux/api/web-api'
import { notifyError, notifySuccess } from '../../../common/features/notificationsManager/Notify'
import useText from '../../../common/hooks/useText'

type AddMeasurementDeviceModalProps = {
    onClose: () => void
    companyId: string
}

type FormData = { serialNumber: string }

export const AddMeasurementDeviceModal: FC<AddMeasurementDeviceModalProps> = memo(({ onClose, companyId }) => {
    const text = useText()
    const [serialNumber, setSerialNumber] = useState('')

    const { currentData: measurementDevices = [], isLoading: measurementDevicesIsLoading } =
        useMeasurementDevicesGetFilteredQuery(
            {
                serialNumber: serialNumber,
                limit: 20,
            },
            { skip: !serialNumber }
        )

    const [addMDToOrganisation] = useCompanyAddMeasurementDeviceToCompanyMutation()

    const { handleSubmit, register } = useForm<FormData>({
        mode: 'all',
    })

    const onSubmit = (data: FormData) => {
        setSerialNumber(data.serialNumber)
    }

    const handleMDAdd = (device: MeasurementDevicesGetModel) => {
        if (device) {
            addMDToOrganisation({
                companyId,
                measurementDeviceCredentials: { serialNumber: device.serialNumber },
            })
                .unwrap()
                .then(() => {
                    onClose()
                    notifySuccess(text('msgMeasurementDeviceWasAdded'))
                })
                .catch(() => notifyError(text('msgMeasurementDeviceCouldNotBeAdded')))
        } else {
            notifyError(text('msgMeasurementDeviceCouldNotBeAdded'))
        }
    }

    return (
        <BootstrapModalLayout
            size={'lg'}
            title={text('lblAddMeasurementDeviceModal')}
            titleButton={
                <Button
                    size="sm"
                    onClick={onClose}
                    primary={false}
                    className="ghost"
                    icon={<CloseIcon size={18} />}
                    data-testid="info-block-close-btn"
                />
            }
        >
            <div className="inline-flex w-full">
                <form data-testid="search-form" id="search-form" className="w-full" onSubmit={handleSubmit(onSubmit)}>
                    <Input
                        data-testid={'serial-number-input'}
                        id={'serial-number-input'}
                        {...register('serialNumber')}
                        labelText={text('lblSerialNumberShortField')}
                    />
                </form>
                <Button
                    type="submit"
                    form="search-form"
                    primary
                    className="ml-4"
                    loading={measurementDevicesIsLoading}
                    data-testid="search-btn"
                    label={text('btnSearch')}
                />
            </div>
            {measurementDevices && measurementDevices.length > 0 && (
                <div className="mt-5 overflow-y-auto max-h-30rem">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHeaderCell>{text('lblSerialNumber')}</TableHeaderCell>
                                <TableHeaderCell>{text('lblName')}</TableHeaderCell>
                                <TableHeaderCell>{text('lblOrganisation')}</TableHeaderCell>
                                <TableHeaderCell>{text('lblAdd')}</TableHeaderCell>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {measurementDevices?.map((device) => (
                                <TableRow key={device.id}>
                                    <TableBodyCell>{device.serialNumber}</TableBodyCell>
                                    <TableBodyCell>{device.name}</TableBodyCell>
                                    <TableBodyCell>{device.companyName}</TableBodyCell>
                                    <TableBodyCell>
                                        <button
                                            data-testid="add-md-btn"
                                            onClick={() => handleMDAdd(device)}
                                            className="btn btn-ok btn-xs"
                                        >
                                            <FontAwesomeIcon icon={faPlus} />
                                        </button>
                                    </TableBodyCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            )}
        </BootstrapModalLayout>
    )
})
